import { Controller } from "@hotwired/stimulus"

// Polls the JIRA import progress endpoint and swaps in the latest progress
// partial until the import reports done, then reloads to reveal the final
// actions. Replaces the inline <script> that used to live on the import page.
// The loop lives and dies with the element: teardown stops it, even mid-request.
//
// Usage: data-controller="import-progress"
//        data-import-progress-url-value="/…/progress"
//        data-import-progress-interval-value="2000"   (optional)
export default class extends Controller {
  static values = { url: String, interval: { type: Number, default: 2000 } }

  connect() {
    this.poll = this.poll.bind(this)
    this.stopped = false
    this.schedule()
  }

  // FD-653: clearTimeout on its own cancels nothing once poll() is already awaiting
  // its fetch — that timer has already fired. Flag the teardown and abort the request
  // so the resolving poll bails instead of re-arming a timer (and, on completion,
  // hard-reloading) whatever page Turbo Drive has since navigated to.
  disconnect() {
    this.stopped = true
    clearTimeout(this.timeout)
    this.abort?.abort()
  }

  schedule() {
    this.timeout = setTimeout(this.poll, this.intervalValue)
  }

  async poll() {
    this.abort = new AbortController()
    let html = null
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "text/html" },
        signal: this.abort.signal
      })
      if (response.ok) html = await response.text()
    } catch {
      // Transient network error (or our own abort) — the guard below decides
      // whether to keep polling; a live element keeps polling rather than giving up.
    }
    // FD-653: the controller may have been torn down while that request was in
    // flight. Bail before touching the DOM, reloading, or re-arming the timer.
    if (this.stopped || !this.element.isConnected) return
    if (html !== null) this.element.innerHTML = html
    if (this.element.querySelector("[data-import-done]")) {
      window.location.reload()
    } else {
      this.schedule()
    }
  }
}
